/**
 * The evidence panel: what was claimed, and the page it was read from, side by side.
 *
 * It is a drawer rather than a dialog on purpose. An analyst checking figures clicks one row after
 * another; a modal would make them close it every time, and the whole point is that verifying the
 * next number costs one click.
 */

import { Suspense, lazy, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, friendlyError } from "../api/client";
import { useFocusReturn } from "../lib/focus";
import { evidenceCaption, evidenceIsExternal } from "../lib/model";
import { Button, ErrorBanner, Spinner } from "./ui";
import VerifyFigure from "./VerifyFigure";
// pdf.js is most of the front-end's weight. The queue and the summary never need it, so it is
// fetched the first time an analyst opens a piece of evidence.
const PdfPage = lazy(() => import("./PdfPage"));

export interface EvidenceRequest {
  evidenceId: string;
  title: string;
  note?: string | null;
  // Present when the evidence backs a figure the analyst can sign off on. The verification lives
  // here, not in the table, because this is where the comparing actually happens: the page is on
  // screen next to the number.
  caseId?: string;
  fieldName?: string;
  period?: string | null;
}

export default function EvidenceDrawer({
  request,
  onClose,
}: {
  request: EvidenceRequest | null;
  onClose: () => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(560);

  const evidence = useQuery({
    queryKey: ["evidence", request?.evidenceId],
    queryFn: () => api.getEvidence(request!.evidenceId),
    enabled: !!request,
  });

  const documentId = evidence.data?.document_id ?? null;
  const file = useQuery({
    queryKey: ["document-file", documentId],
    queryFn: async () => (await api.documentBlob(documentId!)).arrayBuffer(),
    enabled: !!documentId,
    staleTime: Infinity,
  });

  // No atrapa el foco a propósito: el cajón se lee **al lado** del informe, y atraparlo impediría
  // justamente lo que el cajón existe para hacer. Sí entra al abrirse y vuelve al cerrarse.
  useFocusReturn(panelRef, !!request);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    const element = panelRef.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      // Quantised on purpose. Rendering the page makes the panel scroll, the scrollbar takes a
      // dozen pixels, that changes the observed width, and the viewer re-renders — a loop that
      // kept restarting the page it had just drawn.
      const next = Math.max(320, Math.round((entry.contentRect.width - 48) / 24) * 24);
      setWidth((current) => (current === next ? current : next));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [request]);

  if (!request) return null;

  const detail = evidence.data;
  const external = detail ? evidenceIsExternal(detail) : false;

  return (
    <aside
      ref={panelRef}
      role="complementary"
      aria-label="Evidencia"
      className="fixed top-0 right-0 z-40 flex h-full w-[46vw] min-w-[420px] flex-col border-l border-line bg-white shadow-2xl"
    >
      <header className="flex items-start justify-between gap-4 border-b border-line px-6 py-4">
        <div className="min-w-0">
          <div className="text-[0.7rem] font-semibold tracking-wider text-muted uppercase">
            Evidencia
          </div>
          <div className="truncate font-semibold">{request.title}</div>
        </div>
        <Button variant="ghost" onClick={onClose} aria-label="Cerrar evidencia">
          Cerrar
        </Button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
        {evidence.isLoading ?
          <Spinner />
        : null}
        {evidence.isError ?
          <ErrorBanner {...friendlyError(evidence.error)} />
        : null}

        {detail ?
          <>
            <div className={`text-sm ${external ? "text-warn" : "text-muted"}`}>
              {external ? "Fuente externa · " : "Legajo · "}
              {evidenceCaption(detail)}
            </div>

            {detail.quote ?
              <blockquote className="mt-3 border-l-2 border-line pl-3 text-sm text-ink/80">
                {detail.quote}
              </blockquote>
            : null}

            {request.caseId && request.fieldName ?
              <VerifyFigure
                caseId={request.caseId}
                fieldName={request.fieldName}
                period={request.period ?? null}
              />
            : null}

            {request.note ?
              <p className="mt-3 rounded-lg border border-warn-line bg-warn-bg px-3 py-2 text-sm text-warn">
                {request.note}
              </p>
            : null}

            {detail.url ?
              <a
                href={detail.url}
                target="_blank"
                rel="noreferrer noopener"
                className="mt-3 inline-block text-sm text-primary underline"
              >
                Abrir la fuente
              </a>
            : null}

            {documentId ?
              <div className="mt-5">
                {file.isLoading ?
                  <Spinner label="Abriendo el documento…" />
                : null}
                {file.isError ?
                  <ErrorBanner {...friendlyError(file.error)} />
                : null}
                {file.data && detail.page ?
                  <Suspense fallback={<Spinner label="Preparando el visor…" />}>
                    <PdfPage
                      data={file.data}
                      page={detail.page}
                      quote={detail.quote}
                      width={width}
                      location={detail.location}
                    />
                  </Suspense>
                : null}
                {file.data && !detail.page ?
                  <a
                    href={api.documentUrl(documentId)}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-sm text-primary underline"
                  >
                    Abrir el documento completo
                  </a>
                : null}
              </div>
            : null}
          </>
        : null}
      </div>
    </aside>
  );
}
